import React, { useEffect, useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { countriesCode } from "../../Validations/countryCodeData";
import Button from "../../Buttons/Button";
import { showToast } from "../../Toast/Toast";
import Modal from "../../Modal/Modal";

const sizeClasses = {
  xs: "px-2 py-0",
  sm: "px-3 py-1.5",
  md: "px-3 py-2",
  lg: "px-4 py-2.5",
  xl: "px-5 py-3",
};

const fontSizeClasses = {
  xs: "text-[12px]",
  sm: "text-[12px]",
  md: "text-[14px]",
  lg: "text-[16px]",
  xl: "text-[18px]",
};

const countries = countriesCode;

const topCountries = [
  { code: "+1", flag: "🇺🇸", name: "United States" },
  { code: "+91", flag: "🇮🇳", name: "India" },
  { code: "+44", flag: "🇬🇧", name: "United Kingdom" },
  { code: "+43", flag: "🇦🇹", name: "Austria" },
];

const allCountries = [
  ...topCountries,
  ...countries.filter(
    (country) =>
      !topCountries.some((topCountry) => topCountry.code === country.code)
  ),
];

const CustomPhone = ({
  value = [],
  onChange,
  placeholder = "Enter phone number...",
  size = "md",
  rounded = "rounded",
  selectedCountryCode = value[0]?.code || "+1", // If no value is present, default to +1
  disabled = false,
  multiple = false,
  minLength = 9,
  maxLength = 15,
  shadow = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  // const [countryCode, setCountryCode] = useState(selectedCountryCode || "+1");
  const [countryCode, setCountryCode] = useState("+1");

  useEffect(() => {
    if (value[0]?.code) {
      setCountryCode(value[0].code);
    }
  }, [value]);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(value.length - 1); // Track which phone number is being edited
  // Filtered countries for dropdown
  const filteredCountries = allCountries.filter((country) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      country.name.toLowerCase().includes(searchLower) ||
      country.code.includes(searchQuery) ||
      country.flag.includes(searchQuery)
    );
  });

  // Handle country selection (single mode)
  const handleCountrySelect = (country) => {
    setIsOpen(false);
    setSearchQuery("");
    setCountryCode(country.code);
    const newValue =
      value.length > 0
        ? [{ code: country.code, number: value[0]?.number || "" }]
        : [{ code: country.code, number: "" }];
    onChange(newValue);
  };

  // Handle phone number input (single mode)
  const handleNumberChange = (e) => {
    const rawValue = e.target.value;
    if (!/^\d*$/.test(rawValue)) {
      showToast("Please enter numbers only", "warning");
      return;
    }
    const numericValue = rawValue.replace(/\D/g, "");
    const newValue =
      value.length > 0
        ? value.map((item, idx) =>
            idx === 0 ? { ...item, number: numericValue } : item
          )
        : [{ code: countryCode, number: numericValue }];
    onChange(newValue);
  };

  // Handle country selection (multiple mode)
  const handleCountrySelectMulti = (country) => {
    setIsOpen(false);
    setSearchQuery("");
    setCountryCode(country.code);

    const newValue = [...value];
    if (activeIndex >= 0 && activeIndex < value.length) {
      newValue[activeIndex] = { ...newValue[activeIndex], code: country.code };
    } else {
      newValue.push({ code: country.code, number: "" });
      setActiveIndex(newValue.length - 1);
    }
    onChange(newValue);
  };

  // Handle phone number input (multiple mode)
  const handleNumberChangeMultiple = (e) => {
    const rawValue = e.target.value;
    if (!/^\d*$/.test(rawValue)) {
      showToast("Please enter numbers only", "warning");
      return;
    }
    const numericValue = rawValue.replace(/\D/g, "");
    const newValue = [...value];
    if (activeIndex >= 0 && activeIndex < newValue.length) {
      newValue[activeIndex] = {
        ...newValue[activeIndex],
        number: numericValue,
      };
    } else {
      newValue.push({ code: countryCode, number: numericValue });
      setActiveIndex(newValue.length - 1);
    }
    onChange(newValue);
  };

  // Handle Enter key to add a new phone number
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (value.length > 0 && !value[value.length - 1].number) {
        showToast(
          "Please enter a phone number before adding another",
          "warning"
        );
        return;
      }

      const lastNumber = value[value.length - 1]?.number;
      if (lastNumber && (lastNumber.length < 9 || lastNumber.length > 15)) {
        showToast(`Phone number must be between 9 and 15 digits`, "warning");
        return;
      }

      const newPhoneNumber = { code: countryCode, number: "" };
      onChange([...value, newPhoneNumber]);
      setActiveIndex(value.length); // Focus on the new input
    }
  };

  // Handle deleting a specific phone number
  const handleDeletePhone = (index) => {
    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
    setActiveIndex(Math.max(newValue.length - 1, 0));
  };

  // Handle clearing all phone numbers
  const handleClearAll = () => {
    onChange([]);
    setCountryCode(selectedCountryCode);
    setActiveIndex(-1);
  };

  // Handle clicking a phone number in the modal to edit it
  const handleEditPhone = (index) => {
    setActiveIndex(index);
    setCountryCode(value[index].code);
    setIsOpenModal(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filtered = value.filter((item) => item.code && item.number);

  return (
    <div
      className={`flex-1 relative flex rounded border focus:outline-none ${
        isFocused ? disabled && "border-primary outline-none" : ""
      } 
      ${shadow ? "border-gray-50 inputShadow" : "border-gray-300"}
      ${
        disabled
          ? "border-neutrals-200 bg-neutrals-200 text-black cursor-not-allowed"
          : "hover:border-primary focus:border-primary"
      }`}
    >
      {multiple ? (
        <div className="flex-1 flex items-center">
          <div className="relative" ref={dropdownRef}>
            <button
              ref={buttonRef}
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              onFocus={() => setIsFocused(true)}
              disabled={disabled}
              className={`flex-1 h-full flex items-center justify-between border-0 ${
                sizeClasses[size]
              } ${
                fontSizeClasses[size]
              } ${rounded} rounded-r-none border-r-0 w-[70px]
              ${
                disabled
                  ? "bg-neutrals-200 text-black !cursor-not-allowed"
                  : "bg-white"
              }`}
            >
              <div className="flex-1 flex items-center justify-between gap-1">
                <span className="text-gray-500 text-sm leading-none text-center w-full">
                  {countryCode}
                </span>
                <FaChevronDown className="text-gray-400 text-[10px] leading-none" />
              </div>
            </button>

            {isOpen && (
              <div className="list absolute z-10 min-w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg whitespace-nowrap max-h-[200px]">
                <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search country..."
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-primary"
                  />
                </div>
                <div className="overflow-y-auto max-h-[150px]">
                  {filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountrySelectMulti(country)}
                      className="flex items-center w-full gap-2 px-3 py-2 text-left hover:bg-gray-100"
                    >
                      <span>{country.code}</span>
                      <span className="text-sm text-gray-600">
                        {country.name}
                      </span>
                    </button>
                  ))}
                  {filteredCountries.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No countries found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="flex-1 flex">
            <input
              type="tel"
              className={`for-multiple w-full flex-1 pl-0 border-0 focus:outline-none ${
                sizeClasses[size]
              } ${fontSizeClasses[size]} ${rounded} rounded-l-none
              ${
                disabled
                  ? "bg-neutrals-200 text-black cursor-not-allowed"
                  : "bg-white"
              }`}
              onChange={handleNumberChangeMultiple}
              onKeyDown={handleKeyDown}
              placeholder={filtered.length > 0 ? "Add more" : placeholder}
              disabled={disabled}
              value={value[activeIndex]?.number || ""}
            />
          </div>
          {value.length > 1 && (
            <div
              className={`text-gray-500 text-nowrap text-[12px] bg-gray-200 p-0.5 px-1 rounded`}
            >
              {filtered.length} phone
            </div>
          )}
          {value.length > 1 && (
            <div className="px-2">
              <Button
                type="icon"
                icon="close"
                size="xs"
                color="dark"
                variant="text"
                onClick={handleClearAll}
                disabled={disabled}
              />
              <Button
                type="icon"
                icon="list"
                size="xs"
                color="dark"
                variant="text"
                onClick={() => setIsOpenModal(true)}
                disabled={disabled}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="relative" ref={dropdownRef}>
            <button
              ref={buttonRef}
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              onFocus={() => setIsFocused(true)}
              disabled={disabled}
              className={`flex-1 h-full flex items-center justify-between border-0 ${
                sizeClasses[size]
              } ${
                fontSizeClasses[size]
              } ${rounded} rounded-r-none border-r-0 w-[70px]
              ${
                disabled
                  ? "bg-neutrals-200 text-black !cursor-not-allowed"
                  : "bg-white"
              }`}
            >
              <div className="flex-1 flex items-center justify-between gap-1">
                <span className="text-gray-500 text-sm leading-none text-center w-full">
                  {countryCode}
                </span>
                <FaChevronDown className="text-gray-400 text-[10px] leading-none" />
              </div>
            </button>

            {isOpen && (
              <div className="list absolute z-10 min-w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg whitespace-nowrap max-h-[200px]">
                <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search country..."
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-primary"
                  />
                </div>
                <div className="overflow-y-auto max-h-[150px]">
                  {filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountrySelect(country)}
                      className="flex items-center w-full gap-2 px-3 py-2 text-left hover:bg-gray-100"
                    >
                      <span>{country.code}</span>
                      <span className="text-sm text-gray-600">
                        {country.name}
                      </span>
                    </button>
                  ))}
                  {filteredCountries.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No countries found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="flex-1 flex">
            <input
              type="tel"
              className={`flex-1 pl-0 border-0 focus:outline-none ${
                sizeClasses[size]
              } ${fontSizeClasses[size]} ${rounded} rounded-l-none
              ${
                disabled
                  ? "bg-neutrals-200 text-black cursor-not-allowed"
                  : "bg-white"
              }`}
              onChange={handleNumberChange}
              placeholder={placeholder}
              disabled={disabled}
              value={value[0]?.number || ""}
            />
          </div>
        </>
      )}

      <Modal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        title="Phone Numbers"
        width="max-w-2xl"
      >
        <div className="p-1">
          <div className="flex justify-between mb-4">
            <div>
              <p className="text-gray-500">
                {filtered.length} {filtered.length === 1 ? "phone" : "phones"}{" "}
                added
              </p>
            </div>
            <div className="flex gap-2">
              {filtered.length > 0 && (
                <Button
                  title="Clear All"
                  icon="delete"
                  size="sm"
                  color="danger"
                  onClick={handleClearAll}
                />
              )}
            </div>
          </div>

          {filtered.length > 0 ? (
            <div className="max-h-[400px] overflow-y-auto grid grid-cols-2 gap-x-4 gap-y-1">
              {filtered.map((phone, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-1 border-b border-gray-200 last:border-b-0"
                >
                  <div
                    className="flex items-center gap-4 cursor-pointer"
                    onClick={() => handleEditPhone(index)}
                  >
                    <span className="material-icons text-gray-500 text-sm">
                      phone
                    </span>
                    <div>
                      <p className="text-sm">
                        {phone.code} {phone.number || "(No number)"}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="icon"
                    icon="delete"
                    size="xs"
                    color="danger"
                    variant="text"
                    onClick={() => handleDeletePhone(index)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              No phone numbers added
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CustomPhone;
